import React, { useEffect, useState } from 'react'
import { Paperclip, Download, FileText, ImageOff, Loader2 } from 'lucide-react'
import { workspaceAPI } from '../../services/api'

// attId is unique per upload, so object URLs can be cached for the session.
const _blobCache = new Map()   // "noteId/attId" → Promise<objectURL>

function fetchBlobUrl(noteId, attId) {
  const key = `${noteId}/${attId}`
  if (!_blobCache.has(key)) {
    _blobCache.set(
      key,
      workspaceAPI.fetchAttachmentBlob(noteId, attId)
        .then((res) => URL.createObjectURL(res.data))
        .catch((err) => { _blobCache.delete(key); throw err })
    )
  }
  return _blobCache.get(key)
}

function humanSize(bytes) {
  if (bytes == null) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

/**
 * AttachmentBlock — renders an uploaded file with its original bytes.
 * Images render inline; everything else gets a file card with download.
 * Bytes travel through axios (auth header), never a token-in-URL.
 */
export default function AttachmentBlock({ noteId, attachment }) {
  const [url, setUrl] = useState(null)
  const [state, setState] = useState('loading')   // loading | ready | error

  const isImage = attachment?.mime?.startsWith('image/')

  useEffect(() => {
    if (!attachment) return
    let alive = true
    setState('loading')
    fetchBlobUrl(noteId, attachment.id)
      .then((u) => { if (alive) { setUrl(u); setState('ready') } })
      .catch(() => { if (alive) setState('error') })
    return () => { alive = false }
  }, [noteId, attachment])

  if (!attachment) {
    return (
      <div className="my-1 flex items-center gap-2 rounded-xl border border-dashed border-ink-200 bg-ink-50/50 px-4 py-3 text-sm text-ink-400">
        <ImageOff className="w-4 h-4" />
        Attachment reference is missing its file metadata.
      </div>
    )
  }

  const download = async () => {
    const u = url || await fetchBlobUrl(noteId, attachment.id)
    const a = document.createElement('a')
    a.href = u
    a.download = attachment.name
    a.click()
  }

  if (isImage && state !== 'error') {
    return (
      <figure className="my-2">
        {state === 'loading' ? (
          <div className="flex items-center justify-center h-40 rounded-xl border border-ink-100 bg-ink-50/60">
            <Loader2 className="w-5 h-5 text-ink-300 animate-spin" />
          </div>
        ) : (
          <img
            src={url}
            alt={attachment.name}
            className="max-h-[28rem] max-w-full rounded-xl border border-ink-100 shadow-elev-1"
          />
        )}
        <figcaption className="mt-1.5 flex items-center gap-2 text-[11px] text-ink-400">
          <Paperclip className="w-3 h-3" />
          {attachment.name} · {humanSize(attachment.size)}
          <button onClick={download} className="text-accent-600 hover:text-accent-700 font-medium inline-flex items-center gap-0.5">
            <Download className="w-3 h-3" /> Download
          </button>
        </figcaption>
      </figure>
    )
  }

  return (
    <div className="my-1.5 flex items-center gap-3 rounded-xl border border-ink-150 bg-white px-4 py-3 shadow-elev-1 max-w-lg">
      <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-accent-50 shrink-0">
        <FileText className="w-4 h-4 text-accent-600" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-ink-800 truncate">{attachment.name}</div>
        <div className="text-[11px] text-ink-400">
          {humanSize(attachment.size)} · {attachment.mime}
          {state === 'error' && <span className="text-loss-600 font-medium ml-1.5">file unavailable on disk</span>}
        </div>
      </div>
      <button
        onClick={download}
        disabled={state === 'error'}
        className="shrink-0 inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-semibold text-accent-700 bg-accent-50 hover:bg-accent-100 disabled:opacity-40 transition-colors"
      >
        <Download className="w-3.5 h-3.5" /> Download
      </button>
    </div>
  )
}
